import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';

export interface UseOrganizationAccountUsersRelationOptions {
  organizationId: number;
  userEmails: string[];
  enabled?: boolean;
}

type OrganizationAccountUsersRelation = components['schemas']['CheckOrganizationAccountUsersRelationEndpoint_Response'];

export function useOrganizationAccountUsersRelation({
  organizationId,
  userEmails,
  enabled = true,
}: UseOrganizationAccountUsersRelationOptions) {
  const { getAccessToken } = useAuth();

  return useQuery({
    queryKey: ['organization-account-users-relation', organizationId, userEmails],
    queryFn: async ({ signal }) => {
      const client = createApiClient(getAccessToken, {
        cache: 'no-store',
        signal,
      });

      const { data, error } = await client.POST('/api/organizations/{organizationId}/account-users/check', {
        params: {
          path: { organizationId },
        },
        body: { userEmails },
      });

      if (error) {
        throw new Error(error.title || 'Failed to check organization account users relation');
      }

      return (data ?? []) as OrganizationAccountUsersRelation[];
    },
    enabled: enabled && userEmails.length > 0,
    staleTime: 30000,
  });
}
